import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { useXR } from "@react-three/xr";
import { clone } from "three/examples/jsm/utils/SkeletonUtils";
import * as THREE from "three";
import { HAND_JOINTS, readHandPose } from "./handPose";
import { bindHandModelJoints } from "./handModelRig";

function TrackedHand({ side }) {
  const { scene } = useGLTF(`/models/hands/babylon/${side}.glb`);
  const session = useXR((state) => state.session);
  const player = useXR((state) => state.player);
  const gl = useThree((state) => state.gl);
  const root = useRef();
  const rig = useMemo(() => {
    const model = clone(scene);
    const bones = bindHandModelJoints(model, side);
    // Three's built-in matcap fallback shades the normals without scene lights
    // or a texture. Hands stay readable in car-only mode without relighting it.
    const material = new THREE.MeshMatcapMaterial({ color: "#dce8f2", fog: false, toneMapped: false });
    const skeletons = new Set();
    model.traverse((object) => {
      if (object.isMesh) {
        object.material = material;
        object.castShadow = false; object.receiveShadow = false;
        object.frustumCulled = false;
        object.raycast = () => {}; // Visuals never intercept menu selection.
      }
      if (object.isSkinnedMesh) skeletons.add(object.skeleton);
    });
    return { model, material, skeletons, bones, poses: [], matrix: new THREE.Matrix4() };
  }, [scene, side]);
  useEffect(() => () => {
    rig.material.dispose();
    rig.skeletons.forEach((skeleton) => skeleton.dispose());
  }, [rig]);

  useFrame((_, __, frame) => {
    if (!root.current) return;
    root.current.visible = false;
    if (!session || frame?.session !== session || session.visibilityState !== "visible") return;
    const source = Array.from(session.inputSources).find((input) => input.handedness === side && input.hand);
    if (!rig.bones.every(Boolean) || !readHandPose(frame, source, gl.xr.getReferenceSpace(), rig.poses)) return;
    // These WebXR assets use flat joint bones in reference-space coordinates.
    // The same player transform as input/teleport maps them into the showroom.
    player.updateWorldMatrix(true, false);
    root.current.matrix.copy(player.matrixWorld);
    for (let i = 0; i < rig.bones.length; i++) {
      const bone = rig.bones[i];
      rig.matrix.fromArray(rig.poses[i].transform.matrix).decompose(bone.position, bone.quaternion, bone.scale);
    }
    root.current.visible = true;
    root.current.updateMatrixWorld(true);
  });

  return <group ref={root} name={`xr-hand-${side}`} visible={false} matrixAutoUpdate={false} dispose={null}>
    <primitive object={rig.model} />
  </group>;
}

export default function XrHandModels() {
  return <><TrackedHand side="left" /><TrackedHand side="right" /></>;
}
